/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import StoreShell from "@/components/store/StoreShell";
import { getCart, cartTotalMinor, clearCart } from "@/lib/storeCart";
import { submitPublicCheckout } from "@/lib/api";

function money(minor) { return `₱${((minor || 0) / 100).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`; }

const PAYMENT_METHODS = [
  ["cash_on_delivery", "Cash on Delivery"], ["manual_payment", "Bank Transfer / Manual Payment"],
  ["pay_on_pickup", "Pay on Pickup"], ["external_payment", "Other (business will provide instructions)"],
];

export default function StoreCheckout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  // Lazy-initialized from storage so the empty-cart guard below sees the REAL cart on
  // the very first render — a useEffect-based load would run one render too late,
  // making this page always bounce straight back to /cart even with items present.
  const [items, setItems] = useState(() => getCart(slug));
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", address_line1: "", city: "", delivery_method: "delivery", payment_method: "cash_on_delivery", notes: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (items.length === 0) return <Navigate to={`/store/${slug}/cart`} replace />;

  const submit = async () => {
    setError("");
    if (!form.customer_name || (!form.customer_email && !form.customer_phone)) {
      setError("Please enter your name and either an email or phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const confirmation = await submitPublicCheckout(slug, {
        customer_name: form.customer_name, customer_email: form.customer_email || undefined, customer_phone: form.customer_phone || undefined,
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id || undefined, quantity: i.quantity })),
        shipping_address: form.delivery_method === "delivery" ? { line1: form.address_line1, city: form.city } : undefined,
        delivery_method: form.delivery_method, payment_method: form.payment_method, notes: form.notes || undefined,
      });
      clearCart(slug);
      navigate(`/store/${slug}/order-confirmation/${confirmation.order_id}`, { state: confirmation });
    } catch (err) {
      setError(err.response?.data?.detail || "Could not place your order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StoreShell slug={slug}>
      <h1 className="text-2xl font-bold font-display text-[#0F172A] mb-4">Checkout</h1>

      <div className="rounded-2xl border border-[#E2E8F0] p-4 mb-5">
        {items.map((item) => (
          <div key={`${item.product_id}-${item.variant_id}`} className="flex justify-between text-sm py-1">
            <span>{item.name}{item.variant_name ? ` (${item.variant_name})` : ""} × {item.quantity}</span>
            <span>{money(item.unit_price_minor * item.quantity)}</span>
          </div>
        ))}
        <div className="mt-2 flex justify-between border-t border-[#E2E8F0] pt-2 font-bold text-[#0F172A]"><span>Total</span><span>{money(cartTotalMinor(items))}</span></div>
      </div>

      <div className="space-y-3">
        <input placeholder="Full name" value={form.customer_name} onChange={set("customer_name")} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
        <input placeholder="Email" value={form.customer_email} onChange={set("customer_email")} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
        <input placeholder="Phone" value={form.customer_phone} onChange={set("customer_phone")} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />

        <label htmlFor="delivery-method" className="block text-xs font-medium uppercase text-[#475569]">Delivery method</label>
        <select id="delivery-method" value={form.delivery_method} onChange={set("delivery_method")} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm bg-white">
          <option value="delivery">Delivery</option><option value="pickup">Pickup</option>
        </select>

        {form.delivery_method === "delivery" && (
          <>
            <input placeholder="Address" value={form.address_line1} onChange={set("address_line1")} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
            <input placeholder="City" value={form.city} onChange={set("city")} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />
          </>
        )}

        <label htmlFor="payment-method" className="block text-xs font-medium uppercase text-[#475569]">Payment method</label>
        <select id="payment-method" value={form.payment_method} onChange={set("payment_method")} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm bg-white">
          {PAYMENT_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <p className="text-xs text-[#94A3B8]">No online payment is collected here — you'll pay the business directly according to the method you choose.</p>

        <textarea placeholder="Order notes (optional)" value={form.notes} onChange={set("notes")} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" rows={2} />
      </div>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      <button disabled={submitting} onClick={submit} className="mt-5 w-full rounded-xl bg-[#0F172A] px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
        {submitting ? "Placing order…" : "Place Order"}
      </button>
      <Link to={`/store/${slug}/cart`} className="mt-3 block text-center text-sm text-[#475569] hover:underline">Back to cart</Link>
    </StoreShell>
  );
}
